import { createServerFn } from '@tanstack/react-start';
import {
    and,
    arrayContains,
    asc,
    count,
    desc,
    eq,
    gte,
    ilike,
    inArray,
    lt,
    not,
    or,
    sql,
} from 'drizzle-orm';
import { z } from 'zod';

import { getDb } from '@/db/client';
import {
    ads,
    articles,
    authors,
    categories,
    frontpage,
    images,
    jobs,
    tips,
    weather,
    cinemaScreenings,
    crosswordPuzzles,
    sportCompetitions,
    type AdRow,
    type ArticleRow,
    type AuthorRow,
    type CategoryRow,
    type CinemaScreeningRow,
    type CrosswordPuzzleRow,
    type FrontpageRow,
    type ImageRow,
    type JobRow,
    type WeatherRow,
} from '@/db/schema';
import {
    articleBodyPlainText,
    parseArticleBody,
    youtubeVideoId,
} from '@/lib/article-body';
import {
    ALERT_ARTICLE_TEMPLATE,
    ALERT_COVER_FILE_IDS,
} from '@/lib/article-templates';
import { EXTERNAL_CATEGORY_KEY } from '@/lib/external-news';
import { ASHKELON_AREA_HINTS } from '@/lib/cinema-providers';

import { toCard, LIVE_REPORT_MAX_AGE_MS } from '@/lib/article-card';
import { normalizeAdCtaColor } from '@/lib/ads';
import {
    buildBoard,
    DEFAULT_CROSSWORD_COLS,
    DEFAULT_CROSSWORD_ROWS,
    type CrosswordPlacement,
} from '@/lib/ashkelon-crossword';
import {
    FRONTPAGE_HOME,
    FRONTPAGE_MORE,
    placeArticleAtPosition,
    resolveHomepageLayout,
} from '@/lib/frontpage';
import { getArticleById, getLatestHeadlines, getRelatedArticles } from '@/server/content';
import { fillDailySeries } from '@/lib/analytics-series';
import { newArticleId } from '@/lib/ids';
import { requireAdminAccess, requireStaffAccess } from '@/server/functions/auth';
import {
    loadAdsPerformanceById,
    loadSiteAnalyticsOverview,
    type AdPerformanceStats,
} from '@/server/lib/site-analytics';
import {
    deleteArticleVectorSafe,
    syncArticleVectorSafe,
} from '@/server/lib/vectors';

function newId() {
    return crypto.randomUUID();
}

function emptyToNull(v: string | null | undefined) {
    if (v === undefined || v === null) return null;
    const t = v.trim();
    return t === '' ? null : t;
}

function parseCsvIds(value: string | null | undefined): string[] | null {
    if (value === undefined || value === null) return null;
    const parts = value
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    return parts;
}

function toDate(value: string | null | undefined, fallback?: Date) {
    if (!value) return fallback ?? new Date();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? (fallback ?? new Date()) : d;
}

/** Resolve the author linked to an Appwrite console user, if any. */
async function findAuthorIdForConsoleUser(userId: string | undefined | null) {
    const id = userId?.trim();
    if (!id) return null;
    const [row] = await getDb()
        .select({ id: authors.id })
        .from(authors)
        .where(eq(authors.userId, id))
        .limit(1);
    return row?.id ?? null;
}

/**
 * Move an article to a 1-based slot on the home frontpage block.
 * Also drops it from the "more" rail so it is not listed twice.
 * Returns the clamped 1-based position that was applied, or null if no home block.
 */
async function applyArticleFrontpagePosition({
    db,
    articleId,
    position,
    now,
}: {
    db: ReturnType<typeof getDb>;
    articleId: string;
    position: number;
    now: Date;
}): Promise<number | null> {
    const fpRows = await db
        .select()
        .from(frontpage)
        .where(inArray(frontpage.key, [FRONTPAGE_HOME, FRONTPAGE_MORE]));

    const homeFp = fpRows.find((row) => row.key === FRONTPAGE_HOME);
    const moreFp = fpRows.find((row) => row.key === FRONTPAGE_MORE);
    if (!homeFp) return null;

    const prevHomeIds = homeFp.articleIds ?? [];
    const nextHomeIds = placeArticleAtPosition(
        prevHomeIds,
        articleId,
        position,
    );
    const actualPosition = nextHomeIds.indexOf(articleId) + 1;
    const homeChanged =
        nextHomeIds.length !== prevHomeIds.length ||
        nextHomeIds.some((id, i) => id !== prevHomeIds[i]);

    if (homeChanged) {
        await db
            .update(frontpage)
            .set({ articleIds: nextHomeIds, updatedAt: now })
            .where(eq(frontpage.id, homeFp.id));
    }

    const moreIds = moreFp?.articleIds;
    if (moreIds?.includes(articleId)) {
        await db
            .update(frontpage)
            .set({
                articleIds: moreIds.filter((id: string) => id !== articleId),
                updatedAt: now,
            })
            .where(eq(frontpage.id, moreFp.id));
    }

    return actualPosition >= 1 ? actualPosition : null;
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

const OVERVIEW_CHART_DAYS = 180;
const OVERVIEW_DRAFT_DAYS = 7;

export const adminOverviewFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const db = getDb();
        const since = new Date(
            Date.now() - OVERVIEW_CHART_DAYS * 24 * 60 * 60 * 1000,
        );
        const draftSince = new Date(
            Date.now() - OVERVIEW_DRAFT_DAYS * 24 * 60 * 60 * 1000,
        );
        const publishedDay = sql<string>`to_char(${articles.date} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD')`;
        const [
            [a],
            [au],
            [c],
            [f],
            [ad],
            [w],
            [img],
            [tipsCount],
            [tipsUnread],
            [published],
            [unpublished],
            [live],
            [adsActive],
            [competitions],
            [cinemaUpcoming],
            recentArticles,
            draftArticles,
            publishedByDayRows,
            analytics,
        ] = await Promise.all([
            db.select({ value: count() }).from(articles),
            db.select({ value: count() }).from(authors),
            db.select({ value: count() }).from(categories),
            db.select({ value: count() }).from(frontpage),
            db.select({ value: count() }).from(ads),
            db.select({ value: count() }).from(weather),
            db.select({ value: count() }).from(images),
            db.select({ value: count() }).from(tips),
            db
                .select({ value: count() })
                .from(tips)
                .where(eq(tips.status, 'unread')),
            db
                .select({ value: count() })
                .from(articles)
                .where(eq(articles.status, 'published')),
            db
                .select({ value: count() })
                .from(articles)
                .where(eq(articles.status, 'unpublished')),
            db
                .select({ value: count() })
                .from(articles)
                .where(
                    and(
                        eq(articles.live, true),
                        // Match public badge: live only within 24h of publish.
                        gte(
                            articles.date,
                            new Date(Date.now() - LIVE_REPORT_MAX_AGE_MS),
                        ),
                    ),
                ),
            db
                .select({ value: count() })
                .from(ads)
                .where(eq(ads.active, true)),
            db.select({ value: count() }).from(sportCompetitions),
            db
                .select({ value: count() })
                .from(cinemaScreenings)
                .where(gte(cinemaScreenings.startsAt, sql`NOW() - INTERVAL '30 minutes'`)),
            db
                .select({
                    id: articles.id,
                    title: articles.title,
                    status: articles.status,
                    live: articles.live,
                    date: articles.date,
                })
                .from(articles)
                .where(eq(articles.status, 'published'))
                .orderBy(desc(articles.date))
                .limit(10),
            db
                .select({
                    id: articles.id,
                    title: articles.title,
                    live: articles.live,
                    date: articles.date,
                    updatedAt: articles.updatedAt,
                })
                .from(articles)
                .where(
                    and(
                        eq(articles.status, 'unpublished'),
                        gte(articles.updatedAt, draftSince),
                    ),
                )
                .orderBy(desc(articles.updatedAt))
                .limit(10),
            db
                .select({
                    day: publishedDay,
                    value: count(),
                })
                .from(articles)
                .where(
                    and(
                        eq(articles.status, 'published'),
                        gte(articles.date, since),
                    ),
                )
                .groupBy(publishedDay)
                .orderBy(publishedDay),
            loadSiteAnalyticsOverview(),
        ]);

        return {
            articles: a?.value ?? 0,
            published: published?.value ?? 0,
            unpublished: unpublished?.value ?? 0,
            live: live?.value ?? 0,
            authors: au?.value ?? 0,
            categories: c?.value ?? 0,
            frontpage: f?.value ?? 0,
            ads: ad?.value ?? 0,
            adsActive: adsActive?.value ?? 0,
            weather: w?.value ?? 0,
            images: img?.value ?? 0,
            tips: tipsCount?.value ?? 0,
            tipsUnread: tipsUnread?.value ?? 0,
            competitions: competitions?.value ?? 0,
            cinemaUpcoming: cinemaUpcoming?.value ?? 0,
            recentArticles,
            draftArticles,
            draftWindowDays: OVERVIEW_DRAFT_DAYS,
            publishedPerDay: fillDailySeries(
                OVERVIEW_CHART_DAYS,
                publishedByDayRows,
            ),
            analytics,
        };
    },
);

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

const listArticlesSchema = z.object({
    q: z.string().optional(),
    status: z.enum(['all', 'published', 'unpublished']).optional(),
    categoryId: z.string().min(1).optional(),
    authorId: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});

export const adminListArticlesFn = createServerFn({ method: 'GET' })
    .validator(listArticlesSchema)
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const filters = [];
        if (data.status && data.status !== 'all') {
            filters.push(eq(articles.status, data.status));
        }
        if (data.categoryId && data.categoryId !== 'all') {
            filters.push(arrayContains(articles.categoryIds, [data.categoryId]));
        } else {
            filters.push(
                not(arrayContains(articles.categoryKeys, [EXTERNAL_CATEGORY_KEY])),
            );
        }
        if (data.authorId && data.authorId !== 'all') {
            filters.push(arrayContains(articles.authorIds, [data.authorId]));
        }
        if (data.q?.trim()) {
            const q = `%${data.q.trim()}%`;
            filters.push(
                or(
                    ilike(articles.title, q),
                    ilike(articles.trailer, q),
                    ilike(articles.id, q),
                )!,
            );
        }
        const where = filters.length ? and(...filters) : undefined;
        const rows = await db
            .select()
            .from(articles)
            .where(where)
            .orderBy(desc(articles.date))
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(articles)
            .where(where);
        return { rows, total: total[0]?.value ?? 0 };
    });

export const adminListArticlesByIdsFn = createServerFn({ method: 'GET' })
    .validator(z.object({ ids: z.array(z.string().min(1)).max(200) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        if (data.ids.length === 0) return [] as ArticleRow[];
        const db = getDb();
        const rows = await db
            .select()
            .from(articles)
            .where(inArray(articles.id, data.ids));
        const byId = new Map(rows.map((row) => [row.id, row]));
        return data.ids
            .map((id) => byId.get(id))
            .filter((row): row is ArticleRow => Boolean(row));
    });

export const adminGetArticleFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        const [row] = await db
            .select()
            .from(articles)
            .where(eq(articles.id, data.id))
            .limit(1);
        if (!row) return null;

        const imageIds = new Set<string>();
        for (const id of row.images ?? []) {
            if (id) imageIds.add(id);
        }
        for (const section of parseArticleBody(row.body)) {
            if (section.type === 'image' && section.imageId) {
                imageIds.add(section.imageId);
            } else if (section.type === 'gallery') {
                for (const imageId of section.imageIds) {
                    if (imageId) imageIds.add(imageId);
                }
            }
        }
        const imageRows =
            imageIds.size > 0
                ? await db
                      .select()
                      .from(images)
                      .where(inArray(images.id, [...imageIds]))
                : [];

        return { article: row, images: imageRows };
    });

const articleSectionSchema = z.object({
    type: z.enum([
        'paragraph',
        'title',
        'quote',
        'video',
        'link',
        'image',
        'gallery',
        'map',
    ]),
}).passthrough();

const articleWriteSchema = z.object({
    id: z.string().optional(),
    trailer: z.string().nullable().optional(),
    title: z.string().min(1),
    subtitle: z.string().nullable().optional(),
    body: z.array(articleSectionSchema).optional(),
    images: z.string().nullable().optional(),
    coverCaption: z.string().nullable().optional(),
    coverCredit: z.string().nullable().optional(),
    authorIds: z.string().nullable().optional(),
    categoryIds: z.string().nullable().optional(),
    categoryKeys: z.string().nullable().optional(),
    remoteId: z.number().int().nullable().optional(),
    status: z.enum(['published', 'unpublished']),
    date: z.string().min(1),
    live: z.boolean().optional(),
    position: z.number().int().optional(),
    canonical: z.string().nullable().optional(),
    redirect: z.string().nullable().optional(),
});

export const adminUpsertArticleFn = createServerFn({ method: 'POST' })
    .validator(articleWriteSchema)
    .handler(async ({ data }) => {
        const auth = await requireStaffAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newArticleId();
        const body = parseArticleBody(data.body ?? []);
        for (const section of body) {
            if (section.type !== 'video') continue;
            if (!section.url.trim()) {
                throw new Error('מקטע וידאו דורש כתובת YouTube');
            }
            if (!youtubeVideoId(section.url)) {
                throw new Error(
                    'מקטעי וידאו חייבים להכיל כתובת YouTube תקינה בלבד',
                );
            }
        }
        const heroIds = parseCsvIds(data.images) ?? [];

        const existing = data.id
            ? await db
                  .select({ id: articles.id })
                  .from(articles)
                  .where(eq(articles.id, id))
                  .limit(1)
            : [];
        const isCreate = !existing[0];

        let authorIds = parseCsvIds(data.authorIds);
        if (isCreate && (!authorIds || authorIds.length === 0)) {
            const linkedAuthorId = await findAuthorIdForConsoleUser(
                auth.currentUser?.$id,
            );
            if (linkedAuthorId) authorIds = [linkedAuthorId];
        }

        const desiredPosition = auth.isFullAdmin ? (data.position ?? 0) : 0;
        // Frontpage placement implies the article is live; publish if still a draft.
        const status =
            desiredPosition >= 1 ? ('published' as const) : data.status;
        const values: typeof articles.$inferInsert = {
            id,
            trailer: emptyToNull(data.trailer),
            title: data.title.trim(),
            subtitle: emptyToNull(data.subtitle),
            body,
            images: heroIds,
            authorIds,
            categoryIds: parseCsvIds(data.categoryIds),
            categoryKeys: parseCsvIds(data.categoryKeys),
            remoteId: data.remoteId ?? null,
            status,
            date: toDate(data.date),
            live: data.live ?? false,
            // Position is a one-shot frontpage placement; always clear after save.
            position: 0,
            canonical: emptyToNull(data.canonical),
            redirect: emptyToNull(data.redirect),
            updatedAt: now,
        };

        if (existing[0]) {
            await db.update(articles).set(values).where(eq(articles.id, id));
        } else {
            await db.insert(articles).values({
                ...values,
                createdAt: now,
            });
        }

        const coverId = heroIds[0];
        if (coverId && (data.coverCaption !== undefined || data.coverCredit !== undefined)) {
            await db
                .update(images)
                .set({
                    ...(data.coverCaption !== undefined
                        ? { caption: emptyToNull(data.coverCaption) }
                        : {}),
                    ...(data.coverCredit !== undefined
                        ? { credit: emptyToNull(data.coverCredit) }
                        : {}),
                    updatedAt: now,
                })
                .where(eq(images.id, coverId));
        }

        await syncArticleVectorSafe({
            id,
            title: values.title,
            trailer: values.trailer,
            subtitle: values.subtitle,
            body: articleBodyPlainText(body),
            categoryKeys: values.categoryKeys,
            status: values.status,
            date: values.date,
        });

        if (desiredPosition >= 1) {
            await applyArticleFrontpagePosition({
                db,
                articleId: id,
                position: desiredPosition,
                now,
            });
        }

        return { id };
    });

/** Upsert gallery rows from Appwrite Storage file ids; returns image row ids. */
export const adminEnsureImagesFn = createServerFn({ method: 'POST' })
    .validator(
        z.object({
            fileIds: z.array(z.string().min(1)).min(1),
            credits: z.record(z.string(), z.string()).optional(),
            alts: z.record(z.string(), z.string()).optional(),
        }),
    )
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        const now = new Date();
        const unique = [...new Set(data.fileIds.map((id) => id.trim()).filter(Boolean))];
        if (unique.length === 0) return { images: [] as ImageRow[] };

        const credits = data.credits ?? {};
        const alts = data.alts ?? {};

        const existing = await db
            .select()
            .from(images)
            .where(inArray(images.fileId, unique));
        const byFileId = new Map(existing.map((row) => [row.fileId, row]));

        for (const fileId of unique) {
            const credit = emptyToNull(credits[fileId]);
            const alt = emptyToNull(alts[fileId]);
            const found = byFileId.get(fileId);
            if (found) {
                const nextCredit = credit ?? found.credit;
                const nextAlt = alt ?? found.alt;
                if (nextCredit !== found.credit || nextAlt !== found.alt) {
                    await db
                        .update(images)
                        .set({
                            credit: nextCredit,
                            alt: nextAlt,
                            updatedAt: now,
                        })
                        .where(eq(images.id, found.id));
                    byFileId.set(fileId, {
                        ...found,
                        credit: nextCredit,
                        alt: nextAlt,
                        updatedAt: now,
                    });
                }
                continue;
            }
            const id = `img_${fileId}`.slice(0, 36);
            await db
                .insert(images)
                .values({
                    id,
                    fileId,
                    fileIdAi: null,
                    caption: null,
                    credit,
                    alt,
                    width: null,
                    height: null,
                    remoteId: null,
                    createdAt: now,
                    updatedAt: now,
                })
                .onConflictDoNothing();
            let [saved] = await db
                .select()
                .from(images)
                .where(eq(images.id, id))
                .limit(1);
            if (!saved) {
                [saved] = await db
                    .select()
                    .from(images)
                    .where(eq(images.fileId, fileId))
                    .limit(1);
            }
            if (!saved) continue;
            // If a concurrent insert won without credit, apply ours.
            if (
                (credit && !saved.credit?.trim()) ||
                (alt && !saved.alt?.trim())
            ) {
                await db
                    .update(images)
                    .set({
                        credit: saved.credit?.trim() ? saved.credit : credit,
                        alt: saved.alt?.trim() ? saved.alt : alt,
                        updatedAt: now,
                    })
                    .where(eq(images.id, saved.id));
                byFileId.set(fileId, {
                    ...saved,
                    credit: saved.credit?.trim() ? saved.credit : credit,
                    alt: saved.alt?.trim() ? saved.alt : alt,
                    updatedAt: now,
                });
            } else {
                byFileId.set(fileId, saved);
            }
        }

        // Preserve input order (including duplicates mapped to same row).
        const ordered = data.fileIds
            .map((fileId) => byFileId.get(fileId.trim()))
            .filter((row): row is ImageRow => !!row);
        return { images: ordered };
    });

/**
 * Pick a random siren / red-alert cover from past צבע אדום posts
 * (or the curated stock file ids), for the emergency article shortcut.
 */
export const adminPickAlertCoverImageFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireStaffAccess();
        const db = getDb();

        const related = await db
            .select({
                id: images.id,
                fileId: images.fileId,
                fileIdAi: images.fileIdAi,
                caption: images.caption,
                credit: images.credit,
                alt: images.alt,
                width: images.width,
                height: images.height,
                remoteId: images.remoteId,
                createdAt: images.createdAt,
                updatedAt: images.updatedAt,
            })
            .from(articles)
            .innerJoin(
                images,
                sql`${images.id} = ${articles.images}[1]`,
            )
            .where(
                and(
                    or(
                        ilike(articles.title, '%צבע אדום%'),
                        ilike(articles.title, '%התרא%'),
                        eq(
                            articles.title,
                            ALERT_ARTICLE_TEMPLATE.title,
                        ),
                    ),
                    or(
                        ilike(images.caption, '%אזעקה נשמעה באשקלון%'),
                        ilike(images.caption, 'ירי רקטות לעבר%'),
                        inArray(images.fileId, [...ALERT_COVER_FILE_IDS]),
                    ),
                ),
            )
            .limit(200);

        const byFileId = new Map<string, ImageRow>();
        for (const row of related) {
            const caption = row.caption ?? '';
            if (
                caption.includes('נתניהו') ||
                caption.includes('נפילה') ||
                caption.includes('תרגיל') ||
                caption.includes('שווא') ||
                caption.includes('לא דווח')
            ) {
                continue;
            }
            if (!byFileId.has(row.fileId)) {
                byFileId.set(row.fileId, row);
            }
        }

        let candidates = [...byFileId.values()];
        if (candidates.length === 0) {
            const fallback = await db
                .select()
                .from(images)
                .where(inArray(images.fileId, [...ALERT_COVER_FILE_IDS]));
            candidates = fallback;
        }

        if (candidates.length === 0) {
            const now = new Date();
            for (const fileId of ALERT_COVER_FILE_IDS) {
                const id = `img_${fileId}`.slice(0, 36);
                await db
                    .insert(images)
                    .values({
                        id,
                        fileId,
                        fileIdAi: null,
                        caption: ALERT_ARTICLE_TEMPLATE.coverCaption,
                        credit: ALERT_ARTICLE_TEMPLATE.coverCredit,
                        alt: ALERT_ARTICLE_TEMPLATE.coverCaption,
                        width: null,
                        height: null,
                        remoteId: null,
                        createdAt: now,
                        updatedAt: now,
                    })
                    .onConflictDoNothing();
            }
            candidates = await db
                .select()
                .from(images)
                .where(inArray(images.fileId, [...ALERT_COVER_FILE_IDS]));
        }

        if (candidates.length === 0) {
            return null;
        }

        const picked =
            candidates[Math.floor(Math.random() * candidates.length)]!;

        return {
            ...picked,
            caption: picked.caption || ALERT_ARTICLE_TEMPLATE.coverCaption,
            credit: picked.credit || ALERT_ARTICLE_TEMPLATE.coverCredit,
            alt: picked.alt || ALERT_ARTICLE_TEMPLATE.coverCaption,
        } satisfies ImageRow;
    },
);

export const adminDeleteArticleFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        await db.delete(articles).where(eq(articles.id, data.id));
        await deleteArticleVectorSafe(data.id);
        return { ok: true as const };
    });

/** Hydrated article page for admin preview (includes unpublished). */
export const adminPreviewArticleFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const article = await getArticleById(data.id);
        if (!article) return null;
        const [related, latestPool] = await Promise.all([
            getRelatedArticles(article, 5),
            getLatestHeadlines([article.$id], 11),
        ]);
        const relatedIds = new Set(related.map((item) => item.$id));
        const latest = latestPool
            .filter((item) => !relatedIds.has(item.$id))
            .slice(0, 6);
        return {
            article,
            related: related.map(toCard),
            latest: latest.map(toCard),
        };
    });

// ---------------------------------------------------------------------------
// Authors
// ---------------------------------------------------------------------------

export const adminListAuthorsFn = createServerFn({ method: 'GET' })
    .validator(
        z.object({
            q: z.string().optional(),
            limit: z.number().int().min(1).max(200).optional(),
            offset: z.number().int().min(0).optional(),
        }),
    )
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        const limit = data.limit ?? 100;
        const offset = data.offset ?? 0;
        const where = data.q?.trim()
            ? or(
                  ilike(authors.firstName, `%${data.q.trim()}%`),
                  ilike(authors.lastName, `%${data.q.trim()}%`),
                  ilike(authors.email, `%${data.q.trim()}%`),
                  ilike(authors.userId, `%${data.q.trim()}%`),
                  ilike(authors.id, `%${data.q.trim()}%`),
              )
            : undefined;
        const rows = await db
            .select()
            .from(authors)
            .where(where)
            .orderBy(asc(authors.lastName), asc(authors.firstName))
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(authors)
            .where(where);
        return { rows, total: total[0]?.value ?? 0 };
    });

export const adminGetAuthorFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db
            .select()
            .from(authors)
            .where(eq(authors.id, data.id))
            .limit(1);
        return row ?? null;
    });

const authorWriteSchema = z.object({
    id: z.string().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    biography: z.string().nullable().optional(),
    profile: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    facebook: z.string().nullable().optional(),
    twitter: z.string().nullable().optional(),
    instagram: z.string().nullable().optional(),
    pinterest: z.string().nullable().optional(),
    userId: z.string().nullable().optional(),
    remoteId: z.number().int().nullable().optional(),
});

export const adminUpsertAuthorFn = createServerFn({ method: 'POST' })
    .validator(authorWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const values: typeof authors.$inferInsert = {
            id,
            firstName: emptyToNull(data.firstName),
            lastName: emptyToNull(data.lastName),
            title: emptyToNull(data.title),
            biography: emptyToNull(data.biography),
            profile: emptyToNull(data.profile),
            email: emptyToNull(data.email),
            facebook: emptyToNull(data.facebook),
            twitter: emptyToNull(data.twitter),
            instagram: emptyToNull(data.instagram),
            pinterest: emptyToNull(data.pinterest),
            userId: emptyToNull(data.userId),
            remoteId: data.remoteId ?? null,
            updatedAt: now,
        };
        const existing = data.id
            ? await db
                  .select({ id: authors.id })
                  .from(authors)
                  .where(eq(authors.id, id))
                  .limit(1)
            : [];
        if (existing[0]) {
            await db.update(authors).set(values).where(eq(authors.id, id));
        } else {
            await db.insert(authors).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminDeleteAuthorFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(authors).where(eq(authors.id, data.id));
        return { ok: true as const };
    });

// ---------------------------------------------------------------------------
// Images (editorial catalog over Storage files)
// ---------------------------------------------------------------------------

export const adminListImagesFn = createServerFn({ method: 'GET' })
    .validator(
        z.object({
            q: z.string().optional(),
            limit: z.number().int().min(1).max(200).optional(),
            offset: z.number().int().min(0).optional(),
        }),
    )
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const q = data.q?.trim();
        const where = q
            ? or(
                  ilike(images.caption, `%${q}%`),
                  ilike(images.credit, `%${q}%`),
                  ilike(images.alt, `%${q}%`),
                  ilike(images.fileId, `%${q}%`),
                  ilike(images.fileIdAi, `%${q}%`),
                  ilike(images.id, `%${q}%`),
              )
            : undefined;
        const rows = await db
            .select()
            .from(images)
            .where(where)
            .orderBy(desc(images.updatedAt), desc(images.createdAt))
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(images)
            .where(where);
        return { rows, total: total[0]?.value ?? 0 };
    });

export const adminGetImageFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db
            .select()
            .from(images)
            .where(eq(images.id, data.id))
            .limit(1);
        return row ?? null;
    });

const imageWriteSchema = z.object({
    id: z.string().optional(),
    fileId: z.string().min(1),
    fileIdAi: z.string().nullable().optional(),
    caption: z.string().nullable().optional(),
    credit: z.string().nullable().optional(),
    alt: z.string().nullable().optional(),
    width: z.number().int().positive().nullable().optional(),
    height: z.number().int().positive().nullable().optional(),
    remoteId: z.number().int().nullable().optional(),
});

export const adminUpsertImageFn = createServerFn({ method: 'POST' })
    .validator(imageWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const fileId = data.fileId.trim();
        const id =
            data.id?.trim() || `img_${fileId}`.slice(0, 36);

        const values: typeof images.$inferInsert = {
            id,
            fileId,
            fileIdAi: emptyToNull(data.fileIdAi),
            caption: emptyToNull(data.caption),
            credit: emptyToNull(data.credit),
            alt: emptyToNull(data.alt),
            width: data.width ?? null,
            height: data.height ?? null,
            remoteId: data.remoteId ?? null,
            updatedAt: now,
        };

        const existing = data.id
            ? await db
                  .select({ id: images.id })
                  .from(images)
                  .where(eq(images.id, id))
                  .limit(1)
            : await db
                  .select({ id: images.id })
                  .from(images)
                  .where(eq(images.fileId, fileId))
                  .limit(1);

        if (existing[0]) {
            const targetId = existing[0].id;
            await db
                .update(images)
                .set({ ...values, id: targetId })
                .where(eq(images.id, targetId));
            return { id: targetId };
        }

        await db.insert(images).values({ ...values, createdAt: now });
        return { id };
    });

export const adminDeleteImageFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const id = data.id;

        const [heroHit] = await db
            .select({ value: count() })
            .from(articles)
            .where(arrayContains(articles.images, [id]));
        const [bodyHit] = await db
            .select({ value: count() })
            .from(articles)
            .where(sql`${articles.body}::text like ${`%${id}%`}`);

        const refs = (heroHit?.value ?? 0) + (bodyHit?.value ?? 0);
        if (refs > 0) {
            throw new Error(
                'לא ניתן למחוק תמונה המשויכת לכתבות. הסירו אותה מהגלריה או מגוף הכתבה קודם.',
            );
        }

        await db.delete(images).where(eq(images.id, id));
        return { ok: true as const };
    });

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const adminListCategoriesFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireStaffAccess();
        const rows = await getDb()
            .select()
            .from(categories)
            .orderBy(asc(categories.sortOrder), asc(categories.name));
        return { rows };
    },
);

export const adminGetCategoryFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(categories)
            .where(eq(categories.id, data.id))
            .limit(1);
        return row ?? null;
    });

const categoryWriteSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1),
    key: z.string().min(1),
    description: z.string().nullable().optional(),
    picture: z.string().nullable().optional(),
    remoteId: z.number().int().nullable().optional(),
    archived: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});

export const adminUpsertCategoryFn = createServerFn({ method: 'POST' })
    .validator(categoryWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const values: typeof categories.$inferInsert = {
            id,
            name: data.name.trim(),
            key: data.key.trim(),
            description: emptyToNull(data.description),
            picture: emptyToNull(data.picture),
            remoteId: data.remoteId ?? null,
            updatedAt: now,
        };
        if (data.archived !== undefined) values.archived = data.archived;
        if (data.sortOrder !== undefined) values.sortOrder = data.sortOrder;

        const existing = data.id
            ? await db
                  .select({ id: categories.id })
                  .from(categories)
                  .where(eq(categories.id, id))
                  .limit(1)
            : [];
        if (existing[0]) {
            await db.update(categories).set(values).where(eq(categories.id, id));
        } else {
            values.archived = data.archived ?? false;
            if (data.sortOrder === undefined) {
                const [maxRow] = await db
                    .select({
                        value: sql<number>`coalesce(max(${categories.sortOrder}), -1)`,
                    })
                    .from(categories);
                values.sortOrder = Number(maxRow?.value ?? -1) + 1;
            } else {
                values.sortOrder = data.sortOrder;
            }
            await db.insert(categories).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminReorderCategoriesFn = createServerFn({ method: 'POST' })
    .validator(z.object({ ids: z.array(z.string().min(1)).min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        await Promise.all(
            data.ids.map((id, index) =>
                db
                    .update(categories)
                    .set({ sortOrder: index, updatedAt: now })
                    .where(eq(categories.id, id)),
            ),
        );
        return { ok: true as const };
    });

export const adminSetCategoryArchivedFn = createServerFn({ method: 'POST' })
    .validator(
        z.object({
            id: z.string().min(1),
            archived: z.boolean(),
        }),
    )
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb()
            .update(categories)
            .set({ archived: data.archived, updatedAt: new Date() })
            .where(eq(categories.id, data.id));
        return { ok: true as const };
    });

export const adminDeleteCategoryFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(categories).where(eq(categories.id, data.id));
        return { ok: true as const };
    });

// ---------------------------------------------------------------------------
// Frontpage
// ---------------------------------------------------------------------------

/** Ordered articles on the home frontpage block (for article placement UI). */
export const adminGetHomeFrontpageArticlesFn = createServerFn({
    method: 'GET',
}).handler(async () => {
    await requireAdminAccess();
    const db = getDb();
    const [homeFp] = await db
        .select({ articleIds: frontpage.articleIds })
        .from(frontpage)
        .where(eq(frontpage.key, FRONTPAGE_HOME))
        .limit(1);
    const ids = homeFp?.articleIds ?? [];
    if (ids.length === 0) return [] as ArticleRow[];

    const rows = await db
        .select()
        .from(articles)
        .where(inArray(articles.id, ids));
    const byId = new Map(rows.map((row) => [row.id, row]));
    return ids
        .map((id) => byId.get(id))
        .filter((row): row is ArticleRow => Boolean(row));
});

export const adminListFrontpageFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const rows = await getDb()
            .select()
            .from(frontpage)
            .orderBy(asc(frontpage.key));
        return { rows };
    },
);

export const adminGetFrontpageFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(frontpage)
            .where(eq(frontpage.id, data.id))
            .limit(1);
        return row ?? null;
    });

const frontpageWriteSchema = z.object({
    id: z.string().optional(),
    key: z.string().min(1),
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    articleIds: z.string().nullable().optional(),
    layout: z.enum(['columns', 'hero-full']).optional(),
});

export const adminUpsertFrontpageFn = createServerFn({ method: 'POST' })
    .validator(frontpageWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const values: typeof frontpage.$inferInsert = {
            id,
            key: data.key.trim(),
            title: emptyToNull(data.title),
            description: emptyToNull(data.description),
            articleIds: parseCsvIds(data.articleIds),
            layout: resolveHomepageLayout(data.layout),
            updatedAt: now,
        };
        const existing = data.id
            ? await db
                  .select({ id: frontpage.id })
                  .from(frontpage)
                  .where(eq(frontpage.id, id))
                  .limit(1)
            : [];
        if (existing[0]) {
            await db.update(frontpage).set(values).where(eq(frontpage.id, id));
        } else {
            await db.insert(frontpage).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminDeleteFrontpageFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(frontpage).where(eq(frontpage.id, data.id));
        return { ok: true as const };
    });

// ---------------------------------------------------------------------------
// Ads
// ---------------------------------------------------------------------------

const listAdsSchema = z.object({
    days: z.union([z.literal(7), z.literal(30), z.literal(90)]).optional(),
});

export const adminListAdsFn = createServerFn({ method: 'GET' })
    .validator(listAdsSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const statsWindowDays = data.days ?? 7;
        const since = new Date(
            Date.now() - statsWindowDays * 24 * 60 * 60 * 1000,
        );
        const [rows, statsMap] = await Promise.all([
            getDb()
                .select()
                .from(ads)
                .orderBy(asc(ads.sortOrder), desc(ads.updatedAt)),
            loadAdsPerformanceById(since),
        ]);
        const statsByAdId: Record<string, AdPerformanceStats> = {};
        let impressions = 0;
        let clicks = 0;
        let visitors = 0;
        for (const [id, stats] of statsMap) {
            statsByAdId[id] = stats;
            impressions += stats.impressions;
            clicks += stats.clicks;
            visitors += stats.visitors;
        }
        return {
            rows,
            statsByAdId,
            statsWindowDays,
            totals: { impressions, clicks, visitors },
        };
    });

export const adminGetAdFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(ads)
            .where(eq(ads.id, data.id))
            .limit(1);
        return row ?? null;
    });

const adWriteSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1),
    creativeType: z.enum(['image', 'text']),
    mediaId: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    logoMediaId: z.string().nullable().optional(),
    logoUrl: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    ctaLabel: z.string().nullable().optional(),
    ctaColor: z
        .string()
        .nullable()
        .optional()
        .refine(
            (v) =>
                v == null ||
                v === '' ||
                /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim()),
            { message: 'ctaColor must be a hex color' },
        ),
    ctaTextColor: z
        .string()
        .nullable()
        .optional()
        .refine(
            (v) =>
                v == null ||
                v === '' ||
                /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim()),
            { message: 'ctaTextColor must be a hex color' },
        ),
    sponsorName: z.string().nullable().optional(),
    badge: z.string().nullable().optional(),
    secondaryCtaLabel: z.string().nullable().optional(),
    secondaryCtaUrl: z.string().nullable().optional(),
    linkUrl: z.string().min(1),
    placement: z.enum([
        'homepage-sidebar',
        'homepage-leaderboard',
        'article-inline',
        'article-sidebar',
        'category-inline',
    ]),
    size: z.enum(['300x250', '300x600', '320x50', '320x100', '728x90', '970x90']),
    active: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    startsAt: z.string().nullable().optional(),
    endsAt: z.string().nullable().optional(),
});

export const adminUpsertAdFn = createServerFn({ method: 'POST' })
    .validator(adWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const values: typeof ads.$inferInsert = {
            id,
            name: data.name.trim(),
            creativeType: data.creativeType,
            mediaId: emptyToNull(data.mediaId),
            imageUrl: emptyToNull(data.imageUrl),
            logoMediaId: emptyToNull(data.logoMediaId),
            logoUrl: emptyToNull(data.logoUrl),
            title: emptyToNull(data.title),
            description: emptyToNull(data.description),
            ctaLabel: emptyToNull(data.ctaLabel),
            ctaColor: normalizeAdCtaColor(data.ctaColor),
            ctaTextColor: normalizeAdCtaColor(data.ctaTextColor),
            sponsorName: emptyToNull(data.sponsorName),
            badge: emptyToNull(data.badge),
            secondaryCtaLabel: emptyToNull(data.secondaryCtaLabel),
            secondaryCtaUrl: emptyToNull(data.secondaryCtaUrl),
            linkUrl: data.linkUrl.trim(),
            placement: data.placement,
            size: data.size,
            active: data.active ?? true,
            sortOrder: data.sortOrder ?? 0,
            startsAt: data.startsAt ? toDate(data.startsAt) : null,
            endsAt: data.endsAt ? toDate(data.endsAt) : null,
            updatedAt: now,
        };
        const existing = data.id
            ? await db
                  .select({ id: ads.id })
                  .from(ads)
                  .where(eq(ads.id, data.id))
                  .limit(1)
            : [];
        if (existing[0]) {
            await db.update(ads).set(values).where(eq(ads.id, id));
        } else {
            await db.insert(ads).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminSetAdActiveFn = createServerFn({ method: 'POST' })
    .validator(
        z.object({
            id: z.string().min(1),
            active: z.boolean(),
        }),
    )
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb()
            .update(ads)
            .set({ active: data.active, updatedAt: new Date() })
            .where(eq(ads.id, data.id));
        return { ok: true as const };
    });

export const adminDeleteAdFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(ads).where(eq(ads.id, data.id));
        return { ok: true as const };
    });

export const adminDuplicateAdFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [source] = await db
            .select()
            .from(ads)
            .where(eq(ads.id, data.id))
            .limit(1);
        if (!source) {
            throw new Error('המודעה לא נמצאה');
        }
        const now = new Date();
        const id = newId();
        const suffix = ' (העתק)';
        const maxName = 200 - suffix.length;
        const baseName = source.name.slice(0, maxName);
        await db.insert(ads).values({
            ...source,
            id,
            name: `${baseName}${suffix}`,
            active: false,
            createdAt: now,
            updatedAt: now,
        });
        return { id };
    });

// ---------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------

export const adminListWeatherFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const rows = await getDb()
            .select()
            .from(weather)
            .orderBy(
                desc(weather.forecastDate),
                asc(weather.sortOrder),
                asc(weather.locationName),
            );
        return { rows };
    },
);

export const adminGetWeatherFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(weather)
            .where(eq(weather.id, data.id))
            .limit(1);
        return row ?? null;
    });

const weatherWriteSchema = z.object({
    id: z.string().optional(),
    locationKey: z.string().min(1),
    locationName: z.string().min(1),
    stationId: z.number().int().nullable().optional(),
    forecastDate: z.string().min(1),
    temp: z.number().nullable().optional(),
    tempMin: z.number().nullable().optional(),
    tempMax: z.number().nullable().optional(),
    humidity: z.number().nullable().optional(),
    rain: z.number().nullable().optional(),
    windSpeed: z.number().nullable().optional(),
    windDirectionDeg: z.number().int().nullable().optional(),
    weatherCode: z.number().int().nullable().optional(),
    description: z.string().nullable().optional(),
    iconClass: z.string().nullable().optional(),
    sortOrder: z.number().int().optional(),
    observedAt: z.string().nullable().optional(),
});

export const adminUpsertWeatherFn = createServerFn({ method: 'POST' })
    .validator(weatherWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const locationKey = data.locationKey.trim();
        const forecastDate = data.forecastDate.trim();

        const conflict = await db
            .select({ id: weather.id })
            .from(weather)
            .where(
                and(
                    eq(weather.locationKey, locationKey),
                    eq(weather.forecastDate, forecastDate),
                ),
            )
            .limit(1);
        if (conflict[0] && conflict[0].id !== id) {
            throw new Error(
                `כבר קיימת שורה למיקום ${locationKey} בתאריך ${forecastDate}`,
            );
        }

        const values: typeof weather.$inferInsert = {
            id,
            locationKey,
            locationName: data.locationName.trim(),
            stationId: data.stationId ?? null,
            forecastDate,
            temp: data.temp ?? null,
            tempMin: data.tempMin ?? null,
            tempMax: data.tempMax ?? null,
            humidity: data.humidity ?? null,
            rain: data.rain ?? null,
            windSpeed: data.windSpeed ?? null,
            windDirectionDeg: data.windDirectionDeg ?? null,
            weatherCode: data.weatherCode ?? null,
            description: emptyToNull(data.description),
            iconClass: emptyToNull(data.iconClass),
            sortOrder: data.sortOrder ?? 0,
            observedAt: data.observedAt ? toDate(data.observedAt) : null,
            updatedAt: now,
        };
        const existing = data.id
            ? await db
                  .select({ id: weather.id })
                  .from(weather)
                  .where(eq(weather.id, id))
                  .limit(1)
            : [];
        if (existing[0]) {
            await db.update(weather).set(values).where(eq(weather.id, id));
        } else {
            await db.insert(weather).values({ ...values, createdAt: now });
        }
        return { id };
    });

export const adminDeleteWeatherFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(weather).where(eq(weather.id, data.id));
        return { ok: true as const };
    });

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

const listJobsSchema = z.object({
    q: z.string().optional(),
    status: z.enum(['all', 'running', 'success', 'failure']).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});

export const adminListJobsFn = createServerFn({ method: 'GET' })
    .validator(listJobsSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const filters = [];
        if (data.status && data.status !== 'all') {
            filters.push(eq(jobs.status, data.status));
        }
        if (data.q?.trim()) {
            const q = `%${data.q.trim()}%`;
            filters.push(
                or(
                    ilike(jobs.name, q),
                    ilike(jobs.message, q),
                    ilike(jobs.error, q),
                    ilike(jobs.id, q),
                    ilike(jobs.executionId, q),
                )!,
            );
        }
        const where = filters.length ? and(...filters) : undefined;
        const rows = await db
            .select()
            .from(jobs)
            .where(where)
            .orderBy(desc(jobs.startedAt))
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(jobs)
            .where(where);
        return { rows, total: total[0]?.value ?? 0 };
    });

// ---------------------------------------------------------------------------
// Cinema screenings (synced showtimes)
// ---------------------------------------------------------------------------

const UPCOMING_CINEMA_CUTOFF = sql`NOW() - INTERVAL '30 minutes'`;

const listCinemaScreeningsSchema = z.object({
    q: z.string().optional(),
    provider: z.string().optional(),
    cinema: z.string().optional(),
    when: z.enum(['upcoming', 'past', 'all']).optional(),
    nearby: z.boolean().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});

function buildCinemaScreeningFilters(data: z.infer<typeof listCinemaScreeningsSchema>) {
    const filters = [];
    const when = data.when ?? 'upcoming';

    if (when === 'upcoming') {
        filters.push(gte(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF));
    } else if (when === 'past') {
        filters.push(lt(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF));
    }

    if (data.provider && data.provider !== 'all') {
        filters.push(eq(cinemaScreenings.provider, data.provider));
    }

    if (data.cinema && data.cinema !== 'all') {
        filters.push(eq(cinemaScreenings.cinemaName, data.cinema));
    }

    if (data.nearby) {
        filters.push(
            or(
                ...ASHKELON_AREA_HINTS.map((hint) =>
                    ilike(cinemaScreenings.cinemaName, `%${hint}%`),
                ),
            )!,
        );
    }

    if (data.q?.trim()) {
        const q = `%${data.q.trim()}%`;
        filters.push(
            or(
                ilike(cinemaScreenings.movieTitle, q),
                ilike(cinemaScreenings.cinemaName, q),
                ilike(cinemaScreenings.auditorium, q),
                ilike(cinemaScreenings.providerLabel, q),
                ilike(cinemaScreenings.format, q),
            )!,
        );
    }

    return filters;
}

export const adminListCinemaScreeningsFn = createServerFn({ method: 'GET' })
    .validator(listCinemaScreeningsSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const when = data.when ?? 'upcoming';
        const filters = buildCinemaScreeningFilters(data);
        const where = filters.length ? and(...filters) : undefined;
        const order =
            when === 'past'
                ? desc(cinemaScreenings.startsAt)
                : asc(cinemaScreenings.startsAt);

        const rows = await db
            .select()
            .from(cinemaScreenings)
            .where(where)
            .orderBy(order)
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(cinemaScreenings)
            .where(where);

        return { rows, total: total[0]?.value ?? 0 };
    });

export const adminCinemaStatsFn = createServerFn({ method: 'GET' }).handler(async () => {
    await requireAdminAccess();
    const db = getDb();

    const [upcomingTotal, pastTotal, lastSync, byProvider, topCinemas] = await Promise.all([
        db
            .select({ value: count() })
            .from(cinemaScreenings)
            .where(gte(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF)),
        db
            .select({ value: count() })
            .from(cinemaScreenings)
            .where(lt(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF)),
        db
            .select({ value: sql<Date | null>`MAX(${cinemaScreenings.syncedAt})` })
            .from(cinemaScreenings),
        db
            .select({
                provider: cinemaScreenings.provider,
                providerLabel: cinemaScreenings.providerLabel,
                value: count(),
            })
            .from(cinemaScreenings)
            .where(gte(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF))
            .groupBy(cinemaScreenings.provider, cinemaScreenings.providerLabel)
            .orderBy(desc(count())),
        db
            .select({
                cinemaName: cinemaScreenings.cinemaName,
                provider: cinemaScreenings.provider,
                value: count(),
            })
            .from(cinemaScreenings)
            .where(gte(cinemaScreenings.startsAt, UPCOMING_CINEMA_CUTOFF))
            .groupBy(cinemaScreenings.cinemaName, cinemaScreenings.provider)
            .orderBy(desc(count()))
            .limit(200),
    ]);

    return {
        upcoming: upcomingTotal[0]?.value ?? 0,
        past: pastTotal[0]?.value ?? 0,
        lastSyncedAt: lastSync[0]?.value ?? null,
        byProvider: byProvider.map((row) => ({
            provider: row.provider,
            label: row.providerLabel,
            count: row.value,
        })),
        cinemas: topCinemas.map((row) => ({
            name: row.cinemaName,
            provider: row.provider,
            count: row.value,
        })),
    };
});

const deleteCinemaScreeningSchema = z.object({
    id: z.string().min(1),
});

export const adminDeleteCinemaScreeningFn = createServerFn({ method: 'POST' })
    .validator(deleteCinemaScreeningSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        await db.delete(cinemaScreenings).where(eq(cinemaScreenings.id, data.id));
        return { ok: true };
    });

export const adminPrunePastCinemaScreeningsFn = createServerFn({ method: 'POST' }).handler(
    async () => {
        await requireAdminAccess();
        const db = getDb();
        const cutoff = sql`NOW() - INTERVAL '1 day'`;
        const [{ value: toDelete }] = await db
            .select({ value: count() })
            .from(cinemaScreenings)
            .where(lt(cinemaScreenings.startsAt, cutoff));
        if (toDelete > 0) {
            await db.delete(cinemaScreenings).where(lt(cinemaScreenings.startsAt, cutoff));
        }
        return { deleted: toDelete ?? 0 };
    },
);

const crosswordPlacementSchema = z.object({
    id: z.string().min(1),
    text: z.string().min(1),
    answer: z.string().min(1),
    direction: z.enum(['across', 'down']),
    row: z.number().int().min(0),
    col: z.number().int().min(0),
});

const crosswordWriteSchema = z.object({
    id: z.string().optional(),
    puzzleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    puzzleNumber: z.number().int().min(1),
    title: z.string().nullable().optional(),
    rows: z.number().int().min(3).max(30).optional(),
    cols: z.number().int().min(3).max(40).optional(),
    placements: z.array(crosswordPlacementSchema).min(1),
    published: z.boolean().optional(),
});

export const adminListCrosswordPuzzlesFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const rows = await getDb()
            .select()
            .from(crosswordPuzzles)
            .orderBy(desc(crosswordPuzzles.puzzleDate), desc(crosswordPuzzles.puzzleNumber));
        return { rows };
    },
);

export const adminGetCrosswordPuzzleFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(crosswordPuzzles)
            .where(eq(crosswordPuzzles.id, data.id))
            .limit(1);
        return row ?? null;
    });

export const adminUpsertCrosswordPuzzleFn = createServerFn({ method: 'POST' })
    .validator(crosswordWriteSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const now = new Date();
        const id = data.id?.trim() || newId();
        const rows = data.rows ?? DEFAULT_CROSSWORD_ROWS;
        const cols = data.cols ?? DEFAULT_CROSSWORD_COLS;
        const placements = data.placements as CrosswordPlacement[];

        try {
            buildBoard(placements, rows, cols);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'לוח לא תקין';
            throw new Error(message);
        }

        const dateConflict = await db
            .select({ id: crosswordPuzzles.id })
            .from(crosswordPuzzles)
            .where(eq(crosswordPuzzles.puzzleDate, data.puzzleDate))
            .limit(1);
        if (dateConflict[0] && dateConflict[0].id !== id) {
            throw new Error(`כבר קיים תשחץ לתאריך ${data.puzzleDate}`);
        }

        const numberConflict = await db
            .select({ id: crosswordPuzzles.id })
            .from(crosswordPuzzles)
            .where(eq(crosswordPuzzles.puzzleNumber, data.puzzleNumber))
            .limit(1);
        if (numberConflict[0] && numberConflict[0].id !== id) {
            throw new Error(`כבר קיים תשחץ במספר ${data.puzzleNumber}`);
        }

        const payload = {
            puzzleDate: data.puzzleDate,
            puzzleNumber: data.puzzleNumber,
            title: data.title?.trim() || null,
            rows,
            cols,
            placements,
            published: data.published ?? true,
            updatedAt: now,
        };

        const [existing] = await db
            .select({ id: crosswordPuzzles.id })
            .from(crosswordPuzzles)
            .where(eq(crosswordPuzzles.id, id))
            .limit(1);

        if (existing) {
            await db.update(crosswordPuzzles).set(payload).where(eq(crosswordPuzzles.id, id));
        } else {
            await db.insert(crosswordPuzzles).values({ id, ...payload, createdAt: now });
        }

        const [row] = await db
            .select()
            .from(crosswordPuzzles)
            .where(eq(crosswordPuzzles.id, id))
            .limit(1);
        return row!;
    });

export const adminDeleteCrosswordPuzzleFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        await getDb().delete(crosswordPuzzles).where(eq(crosswordPuzzles.id, data.id));
        return { ok: true };
    });

export type {
    AdRow,
    ArticleRow,
    AuthorRow,
    CategoryRow,
    CinemaScreeningRow,
    CrosswordPuzzleRow,
    FrontpageRow,
    ImageRow,
    JobRow,
    WeatherRow,
};
